import { useTranslation } from "react-i18next";
import styles from "../LegalIdVerification.module.scss";
import type { LegalIdVerificationResponse } from "../../../shared/types/verification";
import { splitName } from "../../../shared/utils/nameUtils";
import { usePageView } from "../../../shared/analytics/usePageView";
import { trackClicked } from "../../../shared/analytics/events";
import { DOCUMENT_CAPTURE_EVENTS, DOCUMENT_CAPTURE_SECTIONS } from "../../../shared/analytics/documentCaptureEvents";

interface ConfirmationScreenProps {
  verificationResult: { data: LegalIdVerificationResponse };
  onConfirm: () => void;
  onRetake: () => void;
  consumerLogo?: { src: string; alt: string };
}

// documentTypes that have a dedicated label (e.g. "Driver's license"). Anything
// else — including "unknown" and "residencePermit" — falls back to the generic
// "Document number" title. residencePermit is intentionally excluded: the backend
// returns the card number, not the USCIS number, so a "USCIS number" (or
// "Residence Permit number") label would misrepresent the value to members.
const KNOWN_DOCUMENT_TYPES = new Set([
  "drivingLicense",
  "nationalId",
  "stateId",
  "passport",
  "passcard",
]);

export function ConfirmationScreen({ verificationResult, onConfirm, onRetake }: ConfirmationScreenProps) {
  const { t } = useTranslation();
  usePageView(DOCUMENT_CAPTURE_EVENTS.VIEWED.CONFIRMATION_SCREEN, "section", { section: DOCUMENT_CAPTURE_SECTIONS.CONFIRMATION });

  const attributes = verificationResult.data.results?.attributes;
  const { firstName, middleName, lastName } = splitName(attributes?.nameFirst, attributes?.nameLast);

  // Per product: display the address verbatim (the Platform API returns it
  // ALL-CAPS). No normalization — rendered raw like every other attribute below.
  const address = attributes?.address?.fullAddress;
  const documentNumber = attributes?.documentNumber;
  const documentType = attributes?.documentType;
  const documentNumberLabel =
    documentType && KNOWN_DOCUMENT_TYPES.has(documentType)
      ? t("legalId.confirming.documentNumberTitle", {
          type: t(`legalId.confirming.documentTypes.${documentType}`),
        })
      : t("legalId.confirming.documentNumber");

  const handleConfirm = () => {
    trackClicked(DOCUMENT_CAPTURE_EVENTS.CLICKED.CONFIRM_DOCUMENT, "button", { section: DOCUMENT_CAPTURE_SECTIONS.CONFIRMATION, targetAction: "submit" });
    onConfirm();
  };

  const handleRetake = () => {
    trackClicked(DOCUMENT_CAPTURE_EVENTS.CLICKED.RETAKE_DOCUMENT, "button", { section: DOCUMENT_CAPTURE_SECTIONS.CONFIRMATION, targetAction: "navigate" });
    onRetake();
  };

  return (
    <div className={styles.card}>
      <h1 className={styles.title}>{t('legalId.confirming.heading')}</h1>
      <p className={styles.subtitle}>{t('legalId.confirming.subtitle')}</p>

      <div className={styles.attributesBox}>
        {firstName && (
          <p className={styles.attributeRow}>
            <b className={styles.attributeLabel}>{t('legalId.confirming.firstName')}</b>
            <br aria-hidden="true" />
            {firstName}
          </p>
        )}
        {middleName && (
          <p className={styles.attributeRow}>
            <b className={styles.attributeLabel}>{t('legalId.confirming.middleName')}</b>
            <br aria-hidden="true" />
            {middleName}
          </p>
        )}
        {lastName && (
          <p className={styles.attributeRow}>
            <b className={styles.attributeLabel}>{t('legalId.confirming.lastName')}</b>
            <br aria-hidden="true" />
            {lastName}
          </p>
        )}
        {attributes?.dateOfBirth && (
          <p className={styles.attributeRow}>
            <b className={styles.attributeLabel}>{t('legalId.confirming.dateOfBirth')}</b>
            <br aria-hidden="true" />
            {attributes.dateOfBirth}
          </p>
        )}
        {address && (
          <p className={styles.attributeRow}>
            <b className={styles.attributeLabel}>{t('legalId.confirming.address')}</b>
            <br aria-hidden="true" />
            {address}
          </p>
        )}
        {documentNumber && (
          <p className={styles.attributeRow}>
            <b className={styles.attributeLabel}>{documentNumberLabel}</b>
            <br aria-hidden="true" />
            {documentNumber}
          </p>
        )}
      </div>

      <button className={styles.confirmButton} onClick={handleConfirm} aria-label={t('legalId.confirming.confirmButtonAriaLabel')}>
        {t('legalId.confirming.confirmButton')}
      </button>
      <button className={styles.retakeButton} onClick={handleRetake} aria-label={t('legalId.confirming.retakeButtonAriaLabel')}>
        {t('legalId.confirming.retakeButton')}
      </button>
    </div>
  );
}
